import React, { useEffect, useState, useMemo } from "react";
import toastr from "toastr";
import PartnerClientBondOrders from "./PartnerClientBondOrders";
import IPOOrders from "../Users/Orders/IPOOrders";
import {
    USER_TYPE_BOND_ORDER_HISTORY_ENDPOINTS,
    BondOrderHistoryResponse,
    CalendarCashflowValues,
    IPOOrderHistoryResponse,
    calculateRowHeight,
    computeHeatmapData,
    dedupeBondOrders,
    disclaimerPoints,
    normalizeBondOrdersPayload,
} from "../Users/Orders/helpers";
import {
    makeGETRequestOnUserService,
    makeGETRequest,
} from "../../Api/api";
import { Moment } from "moment";
import moment from "moment";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ExcelImg from "../../images/excel-green.svg";
import { TabPanel } from "../../pages/BidWars";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import DialogActions from "@mui/material/DialogActions";
import { is_gsec_sdl_tBill } from "../../Helpers/functions";

const TAB_OPTIONS = [
    {
        title: "Bonds",
        component: (currentUser: any, user_type: string) => <PartnerClientBondOrders currentUser={currentUser} user_type={user_type} />,
    },
    {
        title: "Public Issue",
        component: (currentUser: any, user_type: string) => <IPOOrders currentUser={currentUser} user_type={user_type} />,
    },
];

export const OrderHistory = ({ currentUser, user_type }: any) => {
    const toastr = require("toastr");
    const [tabValue, setTabValue] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [agreeChecked, setAgreeChecked] = useState(false);
    const [downloadOption, setDownloadOption] = useState<"viewAll" | "currentFY">(
        "viewAll"
    );
    const [bondOrders, setBondOrders] = useState<BondOrderHistoryResponse[]>([]);
    const [ipoOrders, setIPOOrders] = useState<IPOOrderHistoryResponse[]>([]);
    const [profile, setProfile] = useState<any>(null);

    useEffect(() => {
        let mounted = true;
        const fetchProfile = async () => {
            try {
                const response = await makeGETRequestOnUserService(
                    `partneruser/user/get/${currentUser.uuid}/?user_type=${user_type}`
                );

                if (mounted && response.status === 200) {
                    const profile = response.data;
                    setProfile(profile);
                }
            } catch (error) {
                if (mounted) {
                    toastr.error("Failed to fetch profile");
                }
            }
        };
        if (currentUser.uuid) {
            fetchProfile();
        }
        return () => {
            mounted = false;
        };
    }, [currentUser.uuid]);

    useEffect(() => {
        let mounted = true;
        const fetchBondOrders = async () => {
            try {
                const responses = await Promise.allSettled(
                    USER_TYPE_BOND_ORDER_HISTORY_ENDPOINTS.map((buildUrl) =>
                        makeGETRequestOnUserService(buildUrl(currentUser.uuid, user_type))
                    )
                );

                const fulfilledResponses = responses.filter(
                    (
                        response
                    ): response is PromiseFulfilledResult<{ data?: unknown }> =>
                        response.status === "fulfilled"
                );

                if (!fulfilledResponses.length) {
                    throw new Error("Failed to fetch bond orders");
                }

                const combinedOrders = dedupeBondOrders(
                    fulfilledResponses.flatMap((response) =>
                        normalizeBondOrdersPayload(response.value.data)
                    )
                );

                if (mounted) {
                    setBondOrders(combinedOrders);
                }
            } catch (error) {
                if (mounted) {
                    toastr.error("Failed to fetch bond orders");
                }
            }
        };
        if (currentUser.uuid) {
            fetchBondOrders();
        }
        return () => {
            mounted = false;
        };
    }, [currentUser.uuid]);

    useEffect(() => {
        let mounted = true;
        const fetchIPOOrders = async () => {
            try {
                const response = await makeGETRequest(
                    `/ipo/order/history/${currentUser.uuid}/?user_type=${user_type}`
                );
                if (mounted && response.status === 200) {
                    setIPOOrders(response.data);
                }
            } catch (error) {
                if (mounted) {
                    toastr.error("Failed to fetch ipo orders");
                }
            }
        };
        if (currentUser.uuid) {
            fetchIPOOrders();
        }
        return () => {
            mounted = false;
        };
    }, [currentUser.uuid]);

    const customerDetails = useMemo(
        () => [
            {
                key: "Name of Customer:",
                value: `${profile?.firstName} ${profile?.middleName || ""} ${profile?.lastName
                    }`,
            },
            { key: "PAN:", value: profile?.panCard.panCardNumber },
            { key: "Mobile:", value: profile?.mobileNumber },
            { key: "Email-id:", value: profile?.emailId },
        ],
        [profile]
    );

    const heatmapData = useMemo(
        () => computeHeatmapData(bondOrders),
        [bondOrders]
    );

    const flattenedData = useMemo(() => {
        const aggregatedData = new Map<
            string,
            CalendarCashflowValues & { date: Moment; amountAfterTds: number }
        >();

        heatmapData.forEach(({ datesOfMonth }) => {
            datesOfMonth.forEach(({ date, cashflows }) => {
                cashflows.forEach((cashflow) => {
                    const key = `${cashflow.isin}-${date.format("YYYY-MM-DD")}-${cashflow.type
                        }`;
                    const existing = aggregatedData.get(key);

                    if (existing) {
                        existing.amount += cashflow.amount;
                        existing.noOfBonds += cashflow.noOfBonds;
                        existing.amountAfterTds +=
                            cashflow.type === "Interest" &&
                                !(
                                    is_gsec_sdl_tBill(cashflow.securityType) ||
                                    cashflow.taxCategory === "Tax Free"
                                )
                                ? cashflow.amount * 0.9
                                : cashflow.amount;
                    } else {
                        aggregatedData.set(key, {
                            ...cashflow,
                            date,
                            amountAfterTds:
                                cashflow.type === "Interest" &&
                                    !(
                                        is_gsec_sdl_tBill(cashflow.securityType) ||
                                        cashflow.taxCategory === "Tax Free"
                                    )
                                    ? cashflow.amount * 0.9
                                    : cashflow.amount,
                        });
                    }
                });
            });
        });

        return Array.from(aggregatedData.values());
    }, [heatmapData]);

    const { fyStart, fyEnd } = useMemo(() => {
        const currentYear = moment().year();
        return {
            fyStart: moment(`${currentYear}-04-01`),
            fyEnd: moment(`${currentYear + 1}-03-31`),
        };
    }, []);

    const filteredInterest = useMemo(
        () =>
            flattenedData.filter((row) =>
                downloadOption === "currentFY"
                    ? moment(row.date).isBetween(fyStart, fyEnd, "day", "[]")
                    : true
            ),
        [downloadOption, flattenedData, fyEnd, fyStart]
    );

    const sortedData = useMemo(
        () =>
            [...filteredInterest].sort((a, b) => a.date.valueOf() - b.date.valueOf()),
        [filteredInterest]
    );


    return (
        <>
            <Box sx={{ maxWidth: "90%", mx: "auto", zoom: 0.9 }}>
                <Box
                    sx={{
                        mx: [2, 4],
                        mb: 4,
                        px: 2,
                        pt: 2,
                        pb: 0,
                        bgcolor: "#FFFFFF",
                        borderRadius: 4,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                            {TAB_OPTIONS.map((item, index) => (
                                <Button
                                    key={index}
                                    variant={tabValue === index ? "contained" : "outlined"}
                                    onClick={() => setTabValue(index)}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: "none",
                                        color: "secondary.main",
                                        fontWeight: 600,
                                        "& .MuiButton-endIcon": {
                                            height: 20,
                                        },
                                    }}
                                    endIcon={
                                        <Box
                                            component="span"
                                            sx={{
                                                p: 1,
                                                borderRadius: 1,
                                                bgcolor: "secondary.main",
                                                color: "#FFFFFF",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "10px !important",
                                            }}
                                        >
                                            {index === 0
                                                ? bondOrders
                                                    ? bondOrders.length
                                                    : 0
                                                : ipoOrders
                                                    ? ipoOrders.length
                                                    : 0}
                                        </Box>
                                    }
                                >
                                    {item.title}
                                </Button>
                            ))}
                        </Box>
                        <Button
                            variant="outlined"
                            color="success"
                            sx={{ borderRadius: 2, fontWeight: 600, position: "relative" }}
                            onClick={() => setDialogOpen(true)}
                            startIcon={
                                <Box
                                    component="img"
                                    src={ExcelImg}
                                    sx={{ width: 18, height: 18 }}
                                    alt="Excel Icon"
                                />
                            }
                        >
                            My Statements
                        </Button>
                    </Box>
                    <Box>
                        {TAB_OPTIONS.map((item, index) => (
                            <TabPanel value={tabValue} key={index} index={index}>
                                {item.component(currentUser, user_type)}
                            </TabPanel>
                        ))}
                    </Box>
                </Box>
            </Box>
            <Dialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setAgreeChecked(false);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Download Statements
                    <br />
                    <Typography variant="subtitle2" color="textSecondary">
                        Statements for Indicative Interest Received, Securities Purchase
                        /Subscribed and Securities Sold / Matured during the current
                        Financial Year.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreeChecked}
                                onChange={(e) => setAgreeChecked(e.target.checked)}
                                color="primary"
                            />
                        }
                        sx={{ alignItems: "flex-start" }}
                        label={
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{ whiteSpace: "pre-line", lineHeight: 1.5, mt: 1 }}
                            >
                                {`I/We agree, acknowledge and confirm the following:
• I/We shall use this report solely for personal consumption and shall not distribute, share or disclose it to any third party.
• I/We acknowledge that the data contained in the report is derived from the Cash Flow as per the relevant Issue Document and is provided for informational purposes only. Such data shall not be used, presented or relied upon for any other purpose.
• SMEST Capital provides no tax advice, and I/We shall not construe any information contained in this report as tax advice.
• I/We understand and agree that for any queries or concerns related to the report, I/We shall directly contact the Issuer Company. Furthermore, for any queries related to taxation, I/We shall take independent advice from a qualified tax consultant.
• SMEST Capital, its directors, employees and affiliates disclaim any responsibility for the accuracy or completeness of the data contained in this report. I/We agree not to rely on such data for making any investment, financial or tax-related decisions.
• I/We acknowledge that it is my/our sole responsibility to independently verify the accuracy and reliability of the information provided in the report with relevant source documents. Under no circumstances shall SMEST Capital, its directors or affiliates be held liable for any inaccuracies, omissions or consequences from the use of the data.`}
                            </Typography>
                        }
                    />
                    <FormControl fullWidth margin="normal">
                        <InputLabel id="download-option-label">Select Option</InputLabel>
                        <Select
                            labelId="download-option-label"
                            value={downloadOption}
                            onChange={(e) =>
                                setDownloadOption(e.target.value as "viewAll" | "currentFY")
                            }
                            label="Select Option"
                        >
                            <MenuItem value="currentFY">
                                Current Financial Year ({fyStart.format("DD/MM/YYYY")} -{" "}
                                {fyEnd.format("DD/MM/YYYY")})
                            </MenuItem>
                            <MenuItem value="viewAll">View All</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button
                        // onClick={handleExportToExcel}
                        disabled={!agreeChecked}
                        variant="contained"
                        color="primary"
                        sx={{
                            background: agreeChecked
                                ? "linear-gradient(to right, #add100, #7b920a)"
                                : "default",
                            color: "#FFFFFF",
                            boxShadow:
                                "-8px 8px 16px rgba(205, 205, 205, 0.2), 8px -8px 16px rgba(205, 205, 205, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.9), 8px 8px 20px rgba(205, 205, 205, 0.9), inset 1px 1px 2px rgba(255, 255, 255, 0.3), inset -1px -1px 2px rgba(205, 205, 205, 0.5)",
                            py: 1,
                            px: 2,
                            borderRadius: 2,
                        }}
                    >
                        Download
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};